/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Spelling = NO -->*/
/*<!-- vale Hightouch.Spelling = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Field mapping", description = "Map each column in your model to the destination field or property it should populate.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Field mapping",
        description: "Map each column in your model to the destination field or property it should populate.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How field mapping works",
          "depth": 0,
          "id": "how-field-mapping-works"
        }, {
          "value": "Map columns to fields",
          "depth": 0,
          "id": "map-columns-to-fields"
        }, {
          "value": "Create a destination field",
          "depth": 1,
          "id": "create-a-destination-field"
        }, {
          "value": "Advanced mapper",
          "depth": 0,
          "id": "advanced-mapper"
        }, {
          "value": "Column values",
          "depth": 1,
          "id": "column-values"
        }, {
          "value": "Static values",
          "depth": 1,
          "id": "static-values"
        }, {
          "value": "Variable values",
          "depth": 1,
          "id": "variable-values"
        }, {
          "value": "Template mapping",
          "depth": 1,
          "id": "template-mapping"
        }, {
          "value": "Inline mapping",
          "depth": 1,
          "id": "inline-mapping"
        }, {
          "value": "Liquid reference",
          "depth": 1,
          "id": "liquid-reference"
        }, {
          "value": "Extended Liquid use cases",
          "depth": 1,
          "id": "extended-liquid-use-cases"
        }, {
          "value": "Spread JSON columns into fields",
          "depth": 0,
          "id": "spread-json-columns-into-fields"
        }, {
          "value": "Set precedence for multiple columns",
          "depth": 1,
          "id": "set-precedence-for-multiple-columns"
        }, {
          "value": "Transform spread keys and values",
          "depth": 1,
          "id": "transform-spread-keys-and-values"
        }, {
          "value": "Verify the mapping",
          "depth": 0,
          "id": "verify-the-mapping"
        }, {
          "value": "Field mapping updates",
          "depth": 0,
          "id": "field-mapping-updates"
        }, {
          "value": "Model column changes",
          "depth": 1,
          "id": "model-column-changes"
        }, {
          "value": "Destination field changes",
          "depth": 1,
          "id": "destination-field-changes"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      img: "img",
      ol: "ol",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "Field mapping tells Hightouch which destination field each model column should populate."
        }), " You configure it for every sync, after you decide how records are matched."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams configuring a sync"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A sync with a model and a destination selected. Configure ", _jsx(_components.a, {
                href: "/syncs/record-matching",
                children: "record matching"
              }), " first if the sync updates existing records."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "how-field-mapping-works",
        children: "How field mapping works"
      }), "\n", _jsx(_components.p, {
        children: "A mapping relates one column in your model to one field or property in your destination. When the sync runs, Hightouch reads each mapped column and writes its value to the field you paired it with."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Columns, fields, and properties:"
          }), " Hightouch generally uses \"column\" for an\nattribute in your source or model. Destinations may call the same attribute a\n\"field\" or a \"property.\""]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Every destination supports mapping a column to a field. Many also support an ", _jsx(_components.a, {
          href: "#advanced-mapper",
          children: "advanced mapper"
        }), " that sets a field from a constant value, sync metadata, a ", _jsx(_components.a, {
          href: "https://shopify.dev/api/liquid",
          children: "Liquid"
        }), " template, or a nested object or array. Use the advanced mapper when a destination field needs a value your model doesn't already contain as a column."]
      }), "\n", _jsx(_components.h2, {
        id: "map-columns-to-fields",
        children: "Map columns to fields"
      }), "\n", _jsx(_components.p, {
        children: "The mapping section asks which model columns to sync to which destination fields. You can start from either side — whichever matches how you think about the data."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/field-mapping-braze.png",
          alt: "Mapping model columns to Braze fields"
        })
      }), "\n", _jsx(_components.p, {
        children: "To add a mapping:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add mapping"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Choose a model column and the destination field it should populate, in either order."
        }), "\n", _jsx(_components.li, {
          children: "Repeat for each field you want to populate."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Destinations mark required fields so you can confirm you've mapped them before saving."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/required-fields.png",
          alt: "Required destination fields marked with an asterisk"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To map many columns at once, click ", _jsx(_components.strong, {
          children: "Suggest mappings"
        }), ". Hightouch proposes a destination field for each model column by matching similar names — for example, it pairs an ", _jsx(_components.code, {
          children: "email_address"
        }), " column with an ", _jsx(_components.strong, {
          children: "Email"
        }), " field. Review each suggestion before you save, since name matching can pair the wrong fields."]
      }), "\n", _jsx(_components.h3, {
        id: "create-a-destination-field",
        children: "Create a destination field"
      }), "\n", _jsx(_components.p, {
        children: "Some destinations let you populate a field that doesn't exist yet. Where this is supported, enter the new field's name in the destination input and select the option to create it."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/create-custom-field.png",
          alt: "Creating a custom Braze attribute from the destination field input"
        })
      }), "\n", _jsx(_components.h2, {
        id: "advanced-mapper",
        children: "Advanced mapper"
      }), "\n", _jsx(_components.p, {
        children: "The advanced mapper sets a destination field from something other than a single column. Open it by selecting a field's value input, then choose how to supply the value."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/value-type-selector.png",
          alt: "The value type selector in the advanced mapper"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Value type"
            }), _jsx(_components.th, {
              children: "Sets the field from"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Column"
              })
            }), _jsx(_components.td, {
              children: "A model column, the same as basic mapping."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Static value"
              })
            }), _jsx(_components.td, {
              children: "A constant you enter, such as a region code."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Variable value"
              })
            }), _jsx(_components.td, {
              children: "Sync metadata, such as the sync run's timestamp."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Template"
              })
            }), _jsxs(_components.td, {
              children: ["A ", _jsx(_components.a, {
                href: "https://shopify.dev/api/liquid",
                children: "Liquid"
              }), " template that transforms one or more columns."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create an object"
              })
            }), _jsxs(_components.td, {
              children: ["A nested object built from columns. See ", _jsx(_components.a, {
                href: "#inline-mapping",
                children: "inline mapping"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create an array"
              })
            }), _jsxs(_components.td, {
              children: ["An array of objects built from a column. See ", _jsx(_components.a, {
                href: "#inline-mapping",
                children: "inline mapping"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The value types a destination offers depend on what its API accepts. If selecting a field's value input doesn't open these options, the destination doesn't support the advanced mapper. ", _jsx(PylonLink, {
          pylonText: "Hello, I would like to have the advanced mapper on {destination} so that I can {use case}.",
          linkText: "get in touch"
        }), " if you need it for a destination that doesn't have it."]
      }), "\n", _jsx(_components.h3, {
        id: "column-values",
        children: "Column values"
      }), "\n", _jsx(_components.p, {
        children: "Mapping a column value is the same as basic mapping: search for the model column you want and select it for the destination field."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/column-mapping.png",
          alt: "Column mapping in the advanced mapper"
        })
      }), "\n", _jsx(_components.h4, {
        id: "dont-sync-null-values",
        children: "Don't sync null values"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch syncs ", _jsx(_components.code, {
          children: "null"
        }), " values from your model to the destination, which can overwrite an existing destination value with an empty one. To change this, select the settings icon on a column mapping and choose how Hightouch handles nulls:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Always sync"
          }), " — Send ", _jsx(_components.code, {
            children: "null"
          }), " values to the destination. This is the default and can erase existing data."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Don't sync null values"
          }), " — Skip ", _jsx(_components.code, {
            children: "null"
          }), " values so the sync never writes an empty value to the field."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Only write to empty fields"
          }), " — Write the value only when the destination field is currently empty, so the sync never overwrites an existing value."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/null-value-behavior.png",
          alt: "The null value handling options for a column mapping"
        })
      }), "\n", _jsx(_components.p, {
        children: "This setting applies to a single mapping and doesn't affect nested object or array properties."
      }), "\n", _jsx(_components.h3, {
        id: "static-values",
        children: "Static values"
      }), "\n", _jsxs(_components.p, {
        children: ["A static value sets a field to the same constant for every record. For example, you might set every record's ", _jsx(_components.code, {
          children: "region"
        }), " field to ", _jsx(_components.code, {
          children: "EMEA"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/static-value-updated.png",
          alt: "Static value mapping in the advanced mapper"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the advanced mapper, select ", _jsx(_components.strong, {
            children: "Static value"
          }), " and the value's data type: ", _jsx(_components.strong, {
            children: "String"
          }), ", ", _jsx(_components.strong, {
            children: "Number"
          }), ", ", _jsx(_components.strong, {
            children: "Boolean"
          }), ", or ", _jsx(_components.strong, {
            children: "Null"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Enter the value."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Apply"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the destination field to map the static value to."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "variable-values",
        children: "Variable values"
      }), "\n", _jsxs(_components.p, {
        children: ["A variable value sets a field from sync metadata rather than from your data. For example, you might send the sync run's timestamp to an ", _jsx(_components.code, {
          children: "updated_at"
        }), " field. The available variables are:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "model-id"
          }), " (string)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "model-name"
          }), " (string)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "current-timestamp"
          }), " (date/time)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync-id"
          }), " (string)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync-run-id"
          }), " (string)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "operation"
          }), " (string)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/variable-value-updated.png",
          alt: "Variable value mapping in the advanced mapper"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the advanced mapper, select ", _jsx(_components.strong, {
            children: "Variable value"
          }), " and the variable you want, such as ", _jsx(_components.strong, {
            children: "current-timestamp"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Apply"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the destination field to map the variable to."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Realtime and journey-triggered syncs expose additional event-context variables in the same list."
      }), "\n", _jsx(_components.h3, {
        id: "template-mapping",
        children: "Template mapping"
      }), "\n", _jsx(_components.p, {
        children: "The template mapper is convenient when your destination requires data in a specific format, and you want to avoid baking this specificity into your model.\nFor example:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/destinations/webflow",
            children: "Webflow"
          }), " requires slugs not to have any special characters. You can use the template mapper's ", _jsx(_components.code, {
            children: "regex_replace"
          }), " function to remove them."]
        }), "\n", _jsx(_components.li, {
          children: "You want to validate email addresses before sending them to Mailchimp or another email service provider."
        }), "\n", _jsx(_components.li, {
          children: "You want to enable lead routing in your destination using Liquid conditionals without touching your central dbt model."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To create nested objects or arrays, you need to use the ", _jsx(_components.a, {
            href: "#inline-mapping",
            children: "inline\nmapper"
          }), "."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The template mapper uses the ", _jsx(_components.a, {
          href: "https://shopify.dev/api/liquid",
          children: "Liquid templating language"
        }), " to transform column values before they reach the destination."]
      }), "\n", _jsx(_components.p, {
        children: "To create a template mapping:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the advanced mapper, select ", _jsx(_components.strong, {
            children: "Template"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Build your expression in the editor. Insert model columns from the ", _jsx(_components.strong, {
            children: "Columns"
          }), " tab, sync metadata from the ", _jsx(_components.strong, {
            children: "Metadata"
          }), " tab, and functions from the ", _jsx(_components.strong, {
            children: "Filters"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Apply"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the destination field to map the template to."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/template-columns.png",
          alt: "The Template tab of the advanced mapper, showing the Columns tab of the editor"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For example, the expression ", _jsx(_components.code, {
          children: "{{ row['last_name'] | includes: \"Stark\" }}"
        }), " checks whether a row's ", _jsx(_components.code, {
          children: "last_name"
        }), " column contains ", _jsx(_components.code, {
          children: "\"Stark\""
        }), ". If it does, the row is processed; if it doesn't, the row is rejected."]
      }), "\n", _jsx(_components.h4, {
        id: "reference-columns-and-metadata",
        children: "Reference columns and metadata"
      }), "\n", _jsxs(_components.p, {
        children: ["Reference a model column with ", _jsx(_components.code, {
          children: "{{ row['column_name'] }}"
        }), ". To reference ", _jsx(_components.a, {
          href: "#variable-values",
          children: "sync metadata"
        }), " instead, open the ", _jsx(_components.strong, {
          children: "Metadata"
        }), " tab to see the available context variables, such as Model ID, Sync ID, and Operation."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/template-metadata-updated.png",
          alt: "The Metadata tab of the template editor"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["These variables are available on the ", _jsx(_components.code, {
          children: "context"
        }), " object. For example, ", _jsx(_components.code, {
          children: "{{ row['id'] | concat: context['sync_run_id'] }}"
        }), " concatenates a row's ID with the current sync run's ID."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Filters"
        }), " tab lists every available function, including built-in Liquid filters and ", _jsx(_components.a, {
          href: "#liquid-inspired-functions-in-the-advanced-mapper",
          children: "Hightouch-specific functions"
        }), ". Select a filter to insert it into your template."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/template-filters-updated.png",
          alt: "The Filters tab of the template editor"
        })
      }), "\n", _jsx(_components.h4, {
        id: "test-liquid-templates",
        children: "Test Liquid templates"
      }), "\n", _jsxs(_components.p, {
        children: ["Use the ", _jsx(_components.strong, {
          children: "Liquid template tester"
        }), " to preview how your expression evaluates against real rows from your model before you save the mapping. The tester runs your template against up to five sample rows and shows the rendered output for each, so you can catch formatting issues, missing values, or logic errors without triggering a sync."]
      }), "\n", _jsxs(_components.p, {
        children: ["To open the tester, click the ", _jsx(_components.strong, {
          children: "Test template"
        }), " icon in the top-right corner of the template editor. The tester displays the output for each sample row and indicates whether the template parsed successfully. You can edit the template in the tester and apply it when you're satisfied."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/liquid-template-tester.png",
          alt: "The Liquid template tester showing rendered output for sample rows"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The template mapper supports all native Liquid features and adds ", _jsx(_components.a, {
          href: "#liquid-inspired-functions-in-the-advanced-mapper",
          children: "Liquid-inspired"
        }), " functions. For the full list, see the ", _jsx(_components.a, {
          href: "#liquid-reference",
          children: "Liquid reference"
        }), " below."]
      }), "\n", _jsx(_components.h3, {
        id: "inline-mapping",
        children: "Inline mapping"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The inline mapper is available for certain destinations and must be enabled by\nHightouch.", " ", "\n", _jsx(PylonLink, {
            pylonText: "Hello, I'm interested in using the inline mapper with {destination name}. Could you enable it in my workspace?",
            linkText: "Contact us"
          }), " ", "\nto find out whether your destination supports it and to turn it on."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing data to a marketing automation or enterprise resource planning (ERP) tool, you may need to nest related entities. For example, you may have nested items in an invoice or nested entities for a user: associated devices, shipping address, pets, etc. The inline mapper lets you create these nested objects and arrays from your model query results ", _jsx(_components.em, {
          children: "without altering your model definition"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each destination has its own formatting expectations for these nested entities. For example, in Stripe, an ", _jsx(_components.code, {
          children: "invoice"
        }), " object supports an ", _jsx(_components.code, {
          children: "items"
        }), " field that expects an ", _jsx(_components.a, {
          href: "/destinations/netsuite-rest#arrays",
          children: "array of items"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The inline mapper lets you model your data ", _jsx(_components.em, {
          children: "once"
        }), " and format it during sync configuration, rather than creating a separate model for each destination that requires nested objects or arrays."]
      }), "\n", _jsx(_components.h4, {
        id: "create-an-object",
        children: "Create an object"
      }), "\n", _jsxs(_components.p, {
        children: ["The object inline mapper builds an object from your model's columns and maps it to a destination field. This section walks through constructing an object for the ", _jsx(_components.a, {
          href: "https://stripe.com/docs/api/customers/create#create_customer-shipping-address",
          children: "shipping"
        }), " field in Stripe's customer object."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the destination field input, select the field you want to map an object to, for example, ", _jsx(_components.strong, {
            children: "Shipping Address"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["The model column input autopopulates to ", _jsx(_components.strong, {
            children: "Object"
          }), " mode if the selected field is of type ", _jsx(_components.code, {
            children: "object"
          }), ". If the field's type isn't provided, open the advanced mapper and select ", _jsx(_components.strong, {
            children: "Create an object"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/create-an-object.png",
          alt: "The Create an object tab of the advanced mapper"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsx(_components.li, {
          children: "Map each nested destination field to a model column."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/object-mapping.png",
          alt: "An object mapping expanded to show its nested field mappings"
        })
      }), "\n", _jsx(_components.p, {
        children: "With nested mappings like these, Hightouch syncs a payload such as:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"doe@test.com\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"shipping\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"address\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"city\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"state\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"California\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"postal_code\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"94105\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"John Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"phone\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123-456-7890\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "create-an-array",
        children: "Create an array"
      }), "\n", _jsxs(_components.p, {
        children: ["The array inline mapper allows you to construct an array of objects and map them to destination fields using a model column that contains an array of objects. This section walks through constructing a ", _jsx(_components.a, {
          href: "https://stripe.com/docs/api/quotes/object#quote_object-line_items",
          children: "line items"
        }), " array for Stripe's quote object. You would use the same steps for any other object arrays you want to create."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/mapping-data-inlinemapper-example-2-1.png",
          alt: "Array inline mapper example"
        })
      }), "\n", _jsx(_components.h4, {
        id: "required-data-format",
        children: "Required data format"
      }), "\n", _jsxs(_components.p, {
        children: ["To use the inline mapper to create an array of objects, the query results from your ", _jsx(_components.a, {
          href: "/models/creating-models",
          children: "model"
        }), " need to have a certain format. Specifically, they should appear as an array of objects, like the example ", _jsx(_components.strong, {
          children: "items"
        }), " field in the entity relationship diagram (ERD) above."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"item_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"542\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"quantity\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"item_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"631\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"quantity\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "20"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "If you don't already have a data field formatted like this, you can define your model using a SQL query that constructs an array of objects from other fields."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, imagine you have a ", _jsx(_components.code, {
          children: "line_items"
        }), " data table where each row represents an invoice item. The columns are:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "invoice_id"
          }), " for the ID of the invoice the item belongs to"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "customer_id"
          }), " for the ID of the customer who purchased the item"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "amount"
          }), " for the cost of item"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "description"
          }), " for a short description of the item"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "invoice_id"
            }), _jsx(_components.th, {
              children: "customer_id"
            }), _jsx(_components.th, {
              children: "amount"
            }), _jsx(_components.th, {
              children: "description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_1"
            }), _jsx(_components.td, {
              children: "cust_abcd"
            }), _jsx(_components.td, {
              children: "55"
            }), _jsx(_components.td, {
              children: "Waffle Maker"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_1"
            }), _jsx(_components.td, {
              children: "cust_abcd"
            }), _jsx(_components.td, {
              children: "7"
            }), _jsx(_components.td, {
              children: "Dog Chew Toy"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_2"
            }), _jsx(_components.td, {
              children: "cust_efgh"
            }), _jsx(_components.td, {
              children: "12"
            }), _jsx(_components.td, {
              children: "HDMI Cable"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_3"
            }), _jsx(_components.td, {
              children: "cust_abcd"
            }), _jsx(_components.td, {
              children: "45"
            }), _jsx(_components.td, {
              children: "Humidifier Filters"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_3"
            }), _jsx(_components.td, {
              children: "cust_abcd"
            }), _jsx(_components.td, {
              children: "109"
            }), _jsx(_components.td, {
              children: "Electric Kettle"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_3"
            }), _jsx(_components.td, {
              children: "cust_abcd"
            }), _jsx(_components.td, {
              children: "15"
            }), _jsx(_components.td, {
              children: "Wall Calendar"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_4"
            }), _jsx(_components.td, {
              children: "cust_ijkl"
            }), _jsx(_components.td, {
              children: "25"
            }), _jsx(_components.td, {
              children: "Mediterranean Cookbook"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "in_4"
            }), _jsx(_components.td, {
              children: "cust_ijkl"
            }), _jsx(_components.td, {
              children: "10"
            }), _jsx(_components.td, {
              children: "AAA Batteries"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["You want to sync an array of items for each invoice for each customer. For example, for ", _jsx(_components.code, {
          children: "customer_abcd"
        }), " from the preceding table, they would have two separate invoice objects, each with their own ", _jsx(_components.code, {
          children: "items"
        }), " array, one for the invoice with ID ", _jsx(_components.code, {
          children: "in_1"
        }), " and one for the ", _jsx(_components.code, {
          children: "in_3"
        }), " invoice:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"invoice_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"in_1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cust_abcd\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"items\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Waffle Maker\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "55"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Dog Chew Toy\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "7"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"invoice_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"in_3\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cust_abcd\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"items\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Humidifier Filters\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "45"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Electric Kettle\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "109"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Wall Calendar\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "15"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To make these fields available in the inline mapper to construct an ", _jsx(_components.code, {
          children: "items"
        }), " array, you would need to include SQL like this in your model definition:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-SQL",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), "\n  inv.customer_id,\n  inv.invoice_id,\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "array_agg"
          }), "(object_construct(it.", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ")) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " items\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), "\n  line_items inv\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "join"
          }), " items it ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " it.invoice_id ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " inv.invoice_id\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This query selects the ", _jsx(_components.code, {
          children: "customer_id"
        }), " and ", _jsx(_components.code, {
          children: "invoice_id"
        }), " as well as all properties—in this case just description and amount—on each item and then aggregates them into an array of objects. The ", _jsx(_components.code, {
          children: "group by"
        }), " statement groups them based on the ", _jsx(_components.code, {
          children: "customer_id"
        }), " and ", _jsx(_components.code, {
          children: "invoice_id"
        }), " so that each aggregated array is for one invoice for one customer. Without the inline mapper, you would need to write more complex SQL manually constructing each item attribute as part of the item object."]
      }), "\n", _jsx(_components.h4, {
        id: "map-fields",
        children: "Map fields"
      }), "\n", _jsx(_components.p, {
        children: "Once your model query results have an array of objects to pull data from, you can use the inline mapper to configure as many syncs as you need to sync these items."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the sync's mapping section, select the destination field you want to map an array to, for example, ", _jsx(_components.strong, {
            children: "Line items"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["The model column input autopopulates to ", _jsx(_components.strong, {
            children: "Array"
          }), " mode if the selected field is of type ", _jsx(_components.code, {
            children: "array"
          }), ". If the field's type isn't provided, open the advanced mapper and select ", _jsx(_components.strong, {
            children: "Create an array"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["You will be prompted to pick a column that contains an array of objects. Select your desired column from the list of compatible columns. If no columns appear, ensure your model query results conform to the ", _jsx(_components.a, {
            href: "#required-data-format",
            children: "required data format"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/mapping-data/create-an-array.png",
          alt: "The Create an array tab of the advanced mapper, showing the array column picker"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "Select the relevant destination fields you want to map values to and map the nested fields as needed."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/mapping-data-inlinemapper-example-2-3.png",
          alt: "Mapping our example properties to the quotes object in Stripe."
        })
      }), "\n", _jsx(_components.p, {
        children: "The columns available under the array inline mapper are properties of the previously selected column."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch uses the first 100 rows from your query to extract the available properties at each index of the array. To add additional properties that may be missing, type into the input field and select the custom option or press ", _jsx(_components.strong, {
          children: "Enter"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/mapping-data-inlinemapper-example-2-4.png",
          alt: "Mapping our example properties to the quotes object in Stripe"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch only processes properties if the mapped object property at the index\nof the array exists. For example, if ", _jsx(_components.code, {
            children: "your_column[0].price"
          }), " exists and\n", _jsx(_components.code, {
            children: "your_column[1].price"
          }), " doesn't exist, ", _jsx(_components.code, {
            children: "items[0].price"
          }), " will be mapped and\n", _jsx(_components.code, {
            children: "items[1].price"
          }), " won't be mapped. Hightouch syncs ", _jsx(_components.code, {
            children: "null"
          }), " values if you ", _jsx(_components.a, {
            href: "#dont-sync-null-values",
            children: "don't\nchoose to ignore them"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "For each row in your query, the array created using the inline mapper matches the length of the array in your column."
      }), "\n", _jsxs(_components.p, {
        children: ["Given the previous example ", _jsx(_components.a, {
          href: "#create-an-array",
          children: "row"
        }), " and the mappings shown in the screenshot above, Hightouch would sync the following payload:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"line_items\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"price_data\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"currency\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"USD\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"product\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"542\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"quantity\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"price_data\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"currency\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"USD\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"product\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"631\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"quantity\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "20"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "231"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Two items\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "nesting-depth",
        children: "Nesting depth"
      }), "\n", _jsxs(_components.p, {
        children: ["You may need to sync arrays or objects with more deeply nested objects. For example, you may need to sync a ", _jsx(_components.code, {
          children: "Shipping Address"
        }), " object with this format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Jane Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"address\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"address_line_1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123 High Street\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"address_line_2\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"city\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"state\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"California\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"postal_code\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"94105\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"country\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"USA\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"phone_number\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"4151234567\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The \"address\" key expects a further nested object. Hightouch supports nesting up to two levels deep. You can do this by selecting ", _jsx(_components.strong, {
          children: "Create an object"
        }), " or ", _jsx(_components.strong, {
          children: "Create an array"
        }), " while already using the inline mapper."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/nested-mapped-fields.png",
          alt: "Mapping an array in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "liquid-reference",
        children: "Liquid reference"
      }), "\n", _jsx(_components.p, {
        children: "This reference goes over the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Basic ", _jsx(_components.a, {
            href: "#liquid-template-format",
            children: "liquid formatting"
          }), " rules in the advanced mapper"]
        }), "\n", _jsxs(_components.li, {
          children: ["Available ", _jsx(_components.a, {
            href: "#liquid-variables-in-the-advanced-mapper",
            children: "liquid variables"
          }), " and ", _jsx(_components.a, {
            href: "#liquid-inspired-functions-in-the-advanced-mapper",
            children: "functions"
          })]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#extended-liquid-use-cases",
            children: "Extended use cases"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more detailed information, refer to the ", _jsx(_components.a, {
          href: "https://shopify.dev/api/liquid",
          children: "Liquid reference documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "liquid-template-format",
        children: "Liquid template format"
      }), "\n", _jsx(_components.p, {
        children: "Variables follow this format:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'company'"
            }), "]"]
          }), " }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The double handlebars enclose a Liquid snippet that generates an output. The column name, ", _jsx(_components.code, {
          children: "company"
        }), " in the preceding example, should be in single quotes inside the square brackets."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch also provides these alternative syntaxes:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "{{ row.company }}\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "{{ company }}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["These shorter alternatives can be useful when templating messages, such as when syncing to ", _jsx(_components.a, {
          href: "/destinations/slack#templating-messages",
          children: "Slack"
        }), ", ", _jsx(_components.a, {
          href: "/destinations/mattermost#templating-messages",
          children: "Mattermost"
        }), ", ", _jsx(_components.a, {
          href: "/destinations/microsoft-teams#templating-messages",
          children: "Microsoft Teams"
        }), ", or to the ", _jsx(_components.a, {
          href: "/destinations/email#templating-emails",
          children: "SMTP Email"
        }), " destination."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If a model column name contains spaces, reference it with the\nbracket-and-quote syntax ", _jsx(_components.code, {
            children: "row['first name']"
          }), " inside the double handlebars."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Functions follow this format:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'last_name'"
            }), "]"]
          }), " | includes : ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Stark'"
          }), " }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A single pipe (", _jsx(_components.code, {
          children: "|"
        }), ") comes after the variable and before the name of the function and any necessary parameters. See the ", _jsx(_components.a, {
          href: "https://shopify.dev/api/liquid",
          children: "Liquid Reference"
        }), " or ", _jsx(_components.a, {
          href: "https://www.shopify.com/partners/shopify-cheat-sheet?shpxid=7bd156ec-6E04-4D53-42BC-9C614D0E5E0E",
          children: "Liquid Cheatsheet"
        }), " for a complete list of available functions."]
      }), "\n", _jsx(_components.h4, {
        id: "liquid-variables-in-the-advanced-mapper",
        children: "Liquid variables in the advanced mapper"
      }), "\n", _jsx(_components.p, {
        children: "You can use metadata variables in your liquid templates, such as a model's name, all model column names, and the timestamp of a sync's last run."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ruby",
          children: [_jsx(_components.span, {
            className: "hljs-variable",
            children: "$ "
          }), "<column_name>\n", _jsx(_components.span, {
            className: "hljs-variable",
            children: "$ "
          }), "Model Name\n", _jsx(_components.span, {
            className: "hljs-variable",
            children: "$ "
          }), "Timestamp\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can browse the available columns and metadata variables in the ", _jsx(_components.strong, {
          children: "Columns"
        }), " and ", _jsx(_components.strong, {
          children: "Metadata"
        }), " tabs of the template editor."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/mapping-data-liquid-variables.png",
          alt: "Template Mapping"
        })
      }), "\n", _jsx(_components.h4, {
        id: "liquid-inspired-functions-in-the-advanced-mapper",
        children: "Liquid-inspired functions in the advanced mapper"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch provides these non-native functions for you to use out of the box:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Function"
            }), _jsx(_components.th, {
              children: "Syntax"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "base64_decode"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['email']|b64_decode"
              })
            }), _jsx(_components.td, {
              children: "Base64 decode input"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "base64_encode"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['email']|b64_encode"
              })
            }), _jsx(_components.td, {
              children: "Base64 encode input"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "cast"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['phone']|cast:'string'"
              })
            }), _jsx(_components.td, {
              children: "Cast input to specified type. Allowable types are 'string', 'number', 'boolean'"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "includes"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['last_name']|includes:'Stark'"
              })
            }), _jsx(_components.td, {
              children: "Check input for a substring. Row will be rejected if substring isn't found"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "json_construct"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "| json_construct: 'city', row['city'], 'state', row['state_province']"
              })
            }), _jsx(_components.td, {
              children: "Construct JSON object from key/value arguments"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "MD5"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['email']| md5"
              })
            }), _jsx(_components.td, {
              children: "Hash input using MD5 algorithm"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "null_if_empty"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['email']| null_if_empty"
              })
            }), _jsxs(_components.td, {
              children: ["Replace empty strings with ", _jsx(_components.code, {
                children: "null"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "parse"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['first_name']|parse"
              })
            }), _jsx(_components.td, {
              children: "Parse a JSON-formatted string to a JSON object"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "push"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['first_name']|push : '<new array item>'"
              })
            }), _jsx(_components.td, {
              children: "Append an item to an array"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "regex_replace"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['last_name']|regex_replace: '<[a-zA-Z]>', '<replacement>'"
              })
            }), _jsxs(_components.td, {
              children: ["Search and replace substrings of input using ", _jsx(_components.a, {
                href: "https://regexr.com/",
                children: "RegEx"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "regex_test"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['last_name']|regex_test: '<[a-zA-Z]>'"
              })
            }), _jsxs(_components.td, {
              children: ["Check input for a ", _jsx(_components.a, {
                href: "https://regexr.com/",
                children: "RegEx"
              }), " match. Row will be rejected if no match is found using ", _jsx(_components.a, {
                href: "https://regexr.com/",
                children: "RegEx"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sha256"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['email']|sha256"
              })
            }), _jsx(_components.td, {
              children: "Hash input using SHA-256"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sha512"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['email']|sha512"
              })
            }), _jsx(_components.td, {
              children: "Hash input using SHA-512"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "to_date"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['dob']|to_date"
              })
            }), _jsxs(_components.td, {
              children: ["Parse input and return a ", _jsx(_components.a, {
                href: "https://shopify.dev/api/liquid/objects/date",
                children: "Date"
              }), " object (", _jsx(_components.a, {
                href: "https://www.iso.org/iso-8601-date-and-time-format.html",
                children: "ISO 8601"
              }), " format)"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "to_unix"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['date_sent']|to_unix"
              })
            }), _jsxs(_components.td, {
              children: ["Convert input to ", _jsx(_components.a, {
                href: "https://www.unixtimestamp.com/index.php",
                children: "Unix"
              }), " time"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "validate_email"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row['email']|validate_email"
              })
            }), _jsxs(_components.td, {
              children: ["Reject emails that aren't ", _jsx(_components.a, {
                href: "http://haacked.com/archive/2007/08/21/i-knew-how-to-validate-an-email-address-until-i.aspx/",
                children: "RFC 2822"
              }), " compliant email addresses"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "liquid-function-chaining",
        children: "Liquid function chaining"
      }), "\n", _jsxs(_components.p, {
        children: ["You can use more than one Liquid function in a template by ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Method_chaining",
          children: "chaining"
        }), " them. For example, you may want to put a string variable into camel case before hashing it. To chain functions, separate them with a single pipe (", _jsx(_components.code, {
          children: "|"
        }), "):"]
      }), "\n", _jsx(_components.p, {
        children: "Examples:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'full_name'"
            }), "]"]
          }), " | camelcase | sha256 }}\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'email'"
            }), "]"]
          }), " | downcase | validate_email | sha256 }}\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "extended-liquid-use-cases",
        children: "Extended Liquid use cases"
      }), "\n", _jsxs(_components.p, {
        children: ["See Shopify's ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/basics/introduction/",
          children: "Liquid docs"
        }), ", ", _jsx(_components.a, {
          href: "https://www.shopify.com/partners/shopify-cheat-sheet",
          children: "Liquid Cheatsheet"
        }), ", or ", _jsx(_components.a, {
          href: "https://shopify.dev/api/liquid",
          children: "Liquid Reference"
        }), " for a complete list of available functions."]
      }), "\n", _jsx(_components.p, {
        children: "In this section, you can learn how to use Liquid to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["set up ", _jsx(_components.a, {
            href: "#conditional-output",
            children: "conditional outputs"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["output a ", _jsx(_components.code, {
            children: "null"
          }), " value for ", _jsx(_components.a, {
            href: "#conditional-output-for-empty-values",
            children: "empty values"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["set a ", _jsx(_components.a, {
            href: "#default-value",
            children: "default value"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#json-function",
            children: "escape special characters"
          }), " in JSON mappings"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use Liquid to work with different ", _jsx(_components.a, {
          href: "/models/data-types-casting#data-types",
          children: "data types"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Numbers"
          }), ": use ", _jsx(_components.a, {
            href: "#math-functions",
            children: "math functions"
          }), " (such as ", _jsx(_components.code, {
            children: "abs"
          }), " and ", _jsx(_components.code, {
            children: "round"
          }), ") or format ", _jsx(_components.a, {
            href: "#currency-amounts",
            children: "currency amounts"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Booleans"
          }), ": output a ", _jsx(_components.a, {
            href: "#conditional-boolean-output",
            children: "conditional boolean value"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Strings"
          }), ": use ", _jsx(_components.a, {
            href: "#string-functions",
            children: "string functions"
          }), " (such as ", _jsx(_components.code, {
            children: "capitalize"
          }), ", ", _jsx(_components.code, {
            children: "strip"
          }), ", and ", _jsx(_components.code, {
            children: "append"
          }), ") or ", _jsx(_components.a, {
            href: "#isolate-parts-of-a-string",
            children: "isolate parts of a string"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Dates"
          }), ": use the ", _jsx(_components.a, {
            href: "#date-function",
            children: "date function"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Arrays"
          }), ": ", _jsx(_components.a, {
            href: "#construct-a-static-array",
            children: "construct a static array"
          }), ", ", _jsx(_components.a, {
            href: "#sync-an-empty-array",
            children: "sync an empty array"
          }), ", or ", _jsx(_components.a, {
            href: "#convert-an-array-to-a-string",
            children: "convert an array to a string"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Objects"
          }), ": ", _jsx(_components.a, {
            href: "#json-function",
            children: "convert an object to a string"
          }), " or ", _jsx(_components.a, {
            href: "#isolate-object-properties",
            children: "isolate object properties"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "conditional-output",
        children: "Conditional output"
      }), "\n", _jsx(_components.p, {
        children: "You can use Liquid conditionals to specify output based on input. For example, this Liquid snippet:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " if ", _jsx(_components.span, {
            className: "hljs-type",
            children: "row"
          }), "[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'discount_percent'"
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Over"
          }), " half off", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!"
          }), "\n{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " elsif ", _jsx(_components.span, {
            className: "hljs-type",
            children: "row"
          }), "[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'discount_percent'"
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n  ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), " off", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!"
          }), "\n{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " elsif ", _jsx(_components.span, {
            className: "hljs-type",
            children: "row"
          }), "[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'discount_percent'"
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "0"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n  Discounted", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!"
          }), "\n{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "null"
          }), "\n{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " endif ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Outputs the following result in a destination column:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/mapping-data-liquid-conditional-result.png",
          alt: "Liquid Conditional Output"
        })
      }), "\n", _jsx(_components.h4, {
        id: "conditional-output-for-empty-values",
        children: "Conditional output for empty values"
      }), "\n", _jsx(_components.p, {
        children: "Conditionals are also helpful to guard against missing values."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you could use the following Liquid snippet to send a ", _jsx(_components.code, {
          children: "null"
        }), " value for empty values.\nThis is helpful if you need to map a column that exists for some rows but not others:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " if ", _jsx(_components.span, {
            className: "hljs-type",
            children: "row"
          }), "[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'column_name'"
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), "nil ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n{{ ", _jsx(_components.span, {
            className: "hljs-type",
            children: "row"
          }), "[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'column_name'"
          }), "] }}\n{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n{{ ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "null"
          }), " }}\n{", _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " endif ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "%"
          }), "}\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "conditional-boolean-output",
        children: "Conditional boolean output"
      }), "\n", _jsxs(_components.p, {
        children: ["You could also use the ", _jsx(_components.a, {
          href: "https://shopify.dev/docs/api/liquid/tags#assign",
          children: _jsx(_components.code, {
            children: "assign"
          })
        }), " function to output a conditional boolean value:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["{%- if row", _jsx(_components.span, {
            className: "hljs-section",
            children: "['column_name']"
          }), " == \"value\" -%}\n{%- assign ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "variable"
          }), " = ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), " -%}\n{%- else -%}\n{%- assign ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "variable"
          }), " = ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), " -%}\n{%- endif -%}\n{{ variable }}\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "default-value",
        children: "Default value"
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use Liquid to set a ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/filters/default/",
          children: "default"
        }), " value, in case an input value is ", _jsx(_components.code, {
          children: "null"
        }), ", ", _jsx(_components.code, {
          children: "false"
        }), ", or empty. For example, this Liquid snippet sets the ", _jsx(_components.code, {
          children: "revenue"
        }), " field as ", _jsx(_components.code, {
          children: "0"
        }), " if the value is missing or ", _jsx(_components.code, {
          children: "null"
        }), " in a model's row:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'revenue'"
            }), "]"]
          }), " | default: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "0"
          }), " }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Be sure to change ", _jsx(_components.code, {
          children: "revenue"
        }), " to the column name you want to set default values for."]
      }), "\n", _jsx(_components.h4, {
        id: "math-functions",
        children: "Math functions"
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "https://shopify.dev/api/liquid/filters/math-filters",
          children: "Liquid Math Functions"
        }), " for a complete list of math functions.\nIn Hightouch, the absolute value and rounding functions are some of the most commonly used:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.code, {
            children: "abs"
          }), " function to send an absolute value:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["{{ row[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'value'"
          }), "] | abs }}\n"]
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.code, {
            children: "round"
          }), " function to round a value to the nearest integer or a specified number of decimals:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'value'"
            }), "]"]
          }), " | round: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), " }}\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "currency-amounts",
        children: "Currency amounts"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch doesn't support ", _jsx(_components.a, {
          href: "https://shopify.dev/docs/api/liquid/filters/money-filters",
          children: _jsx(_components.code, {
            children: "money"
          })
        }), " functions.\nIf you would like to format currency amounts, such as prices, you can set this up manually by using the following template:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-perl",
          children: ["{%- assign decimals = row[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'price'"
          }), "] | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "split"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | slice: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "0"
          }), ",", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "join"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " -%}\n{%- assign units = row[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'price'"
          }), "] | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "split"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | slice: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "3"
          }), ",", _jsx(_components.span, {
            className: "hljs-number",
            children: "3"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "join"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " -%}\n{%- assign thousands = row[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'price'"
          }), "] | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "split"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | slice: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "6"
          }), ",", _jsx(_components.span, {
            className: "hljs-number",
            children: "3"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "join"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " -%}\n{%- assign millions = row[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'price'"
          }), "] | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "split"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | slice: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "9"
          }), ",", _jsx(_components.span, {
            className: "hljs-number",
            children: "3"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "reverse"
          }), " | ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "join"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " -%}\n\n{%- ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " millions != ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " -%}\n  ${", _jsx(_components.span, {
            className: "hljs-string",
            children: "{millions}"
          }), "},{", _jsx(_components.span, {
            className: "hljs-string",
            children: "{thousands}"
          }), "},{", _jsx(_components.span, {
            className: "hljs-string",
            children: "{units}"
          }), "}.{", _jsx(_components.span, {
            className: "hljs-string",
            children: "{decimals}"
          }), "}\n{%- ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "elsif"
          }), " thousands != ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " -%}\n  ${", _jsx(_components.span, {
            className: "hljs-string",
            children: "{thousands}"
          }), "},{", _jsx(_components.span, {
            className: "hljs-string",
            children: "{units}"
          }), "}.{", _jsx(_components.span, {
            className: "hljs-string",
            children: "{decimals}"
          }), "}\n{%- ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " -%}\n  ${", _jsx(_components.span, {
            className: "hljs-string",
            children: "{units}"
          }), "}.{", _jsx(_components.span, {
            className: "hljs-string",
            children: "{decimals}"
          }), "}\n{%- endif -%}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "To explain this expression:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://shopify.github.io/liquid/filters/split/",
            children: _jsx(_components.code, {
              children: "split"
            })
          }), ": converts the value in the ", _jsx(_components.code, {
            children: "row['price']"
          }), " model column (either ", _jsx(_components.code, {
            children: "number"
          }), " or ", _jsx(_components.code, {
            children: "string"
          }), ") to an array"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://shopify.github.io/liquid/filters/reverse/",
            children: _jsx(_components.code, {
              children: "reverse"
            })
          }), ": reverses the order of items in the array"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://shopify.github.io/liquid/filters/slice/",
            children: _jsx(_components.code, {
              children: "slice"
            })
          }), ": isolates the items in groups of threes"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://shopify.github.io/liquid/filters/reverse/",
            children: _jsx(_components.code, {
              children: "reverse"
            })
          }), ": reverses the order of items in the groups"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://shopify.github.io/liquid/filters/join/",
            children: _jsx(_components.code, {
              children: "join"
            })
          }), ": transforms the single groups of items back into strings"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The second part is a ", _jsx(_components.a, {
          href: "#conditional-output",
          children: "conditional"
        }), " that displays the currency amount in the correct format."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If your model column contains currency amounts without decimal places, make\nsure to remove the ", _jsx(_components.code, {
            children: "decimals"
          }), " variable from the Liquid expression and update\nthe ", _jsx(_components.code, {
            children: "slice"
          }), " functions accordingly."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "string-functions",
        children: "String functions"
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "https://shopify.dev/api/liquid/filters/string-filters",
          children: "Liquid String Functions"
        }), " for a complete list of string functions.\nIn Hightouch, the ", _jsx(_components.code, {
          children: "capitalize"
        }), ", ", _jsx(_components.code, {
          children: "strip"
        }), ", and ", _jsx(_components.code, {
          children: "append"
        }), " functions are some of the most commonly used:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.code, {
            children: "capitalize"
          }), " function to capitalize the first letter of a string:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'title'"
            }), "]"]
          }), " | capitalize }}\n"]
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.code, {
            children: "strip"
          }), " function to remove all whitespace from the left and right sides of a string:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'title'"
            }), "]"]
          }), " | strip }}\n"]
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.code, {
            children: "append"
          }), " function to add a file extension to a string:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'file_name'"
            }), "]"]
          }), " | append: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'.png'"
          }), " }}\n"]
        })
      }), "\n", "\n", _jsx(_components.h4, {
        id: "isolate-parts-of-a-string",
        children: "Isolate parts of a string"
      }), "\n", _jsxs(_components.p, {
        children: ["You can use the Liquid ", _jsxs(_components.a, {
          href: "https://shopify.github.io/liquid/filters/split/",
          children: [_jsx(_components.code, {
            children: "split"
          }), " function"]
        }), " to isolate a certain part of a string.\nFor example, suppose you have a model column that returns URLs, including query parameters.\nAn example value may look like this: ", _jsx(_components.code, {
          children: "www.example.com/?gclid-123"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You may want to send the base URL (", _jsx(_components.code, {
          children: "www.example.com/"
        }), ") as one field and one of the query parameters as another field.\nQuery parameters often include click IDs, such as the ", _jsx(_components.code, {
          children: "gclid"
        }), " or ", _jsx(_components.code, {
          children: "fbclid"
        }), " that you should include on events you send to ", _jsx(_components.a, {
          href: "/destinations/google#conversion-events",
          children: "Google Ads"
        }), " or ", _jsx(_components.a, {
          href: "/destinations/meta-conversions#syncing-events",
          children: "Meta Conversions"
        }), " (or ", _jsx(_components.a, {
          href: "/destinations/meta-offline#syncing-events",
          children: "Meta Offline Conversions"
        }), ") respectively."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the example value ", _jsx(_components.code, {
          children: "www.example.com/?gclid-123"
        }), ", this Liquid snippet would return the base URL, ", _jsx(_components.code, {
          children: "www.example.com/"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'column_name'"
            }), "]"]
          }), " | split: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"?gclid-\""
          }), " | first }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["And this Liquid snippet would return the ", _jsx(_components.code, {
          children: "gclid"
        }), " value, ", _jsx(_components.code, {
          children: "123"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'column_name'"
            }), "]"]
          }), " | split: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"?gclid-\""
          }), " | last }}\n"]
        })
      }), "\n", "\n", _jsx(_components.h4, {
        id: "date-function",
        children: "Date function"
      }), "\n", _jsxs(_components.p, {
        children: ["If your destination expects dates in a different format than your model returns them,\nyou can use the Liquid ", _jsxs(_components.a, {
          href: "https://shopify.github.io/liquid/filters/date/",
          children: [_jsx(_components.code, {
            children: "date"
          }), " function"]
        }), " to reformat them."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, this Liquid snippet ensures that dates from the column are always in the ", _jsx(_components.code, {
          children: "DD/MM/YYYY"
        }), " format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'column_name'"
            }), "]"]
          }), " | date: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'%d/%m/%Y'"
          }), " }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["And this snippet always returns dates in the ", _jsx(_components.code, {
          children: "MM/DD/YYYY"
        }), " format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'column_name'"
            }), "]"]
          }), " | date: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'%m/%d/%Y'"
          }), " }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can also include other parameters to include time and timezone data, by adding ", _jsx(_components.code, {
          children: "%H:%M:%S"
        }), " and ", _jsx(_components.code, {
          children: "%z"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["{{ ", _jsx(_components.span, {
            className: "hljs-type",
            children: "row"
          }), "[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'column_name'"
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "|"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "date"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'%s'"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "|"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "date"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'%Y-%m-%dT%H:%M:%S%z'"
          }), " }}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can use Liquid to change a timestamp's time and date values. For example, you may want to send a date one day earlier than the original. This snippet subtracts 24 hours (represented as 86,400 seconds) from the original date in the column:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["{{ ", _jsx(_components.span, {
            className: "hljs-type",
            children: "row"
          }), "[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'column_name'"
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "|"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "date"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'%s'"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "|"
          }), " minus: ", _jsx(_components.span, {
            className: "hljs-number",
            children: "86400"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "|"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "date"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'%Y-%m-%dT%H:%M:%S'"
          }), " }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To add time to a date, replace ", _jsx(_components.code, {
          children: "minus"
        }), " with ", _jsx(_components.code, {
          children: "plus"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For more examples of how to transform dates and times, refer to this ", _jsx(_components.a, {
          href: "https://support.sweethawk.com/hc/en-us/articles/4412143470221-Liquid-markup-for-date-and-time#index_3",
          children: "documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "construct-a-static-array",
        children: "Construct a static array"
      }), "\n", _jsx(_components.p, {
        children: "You can sync a static array value to your destination by using this Liquid snippet:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{%- assign value = '", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "' | append: '"
            }), _jsx(_components.span, {
              className: "hljs-string",
              children: "\"value1\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"value2\""
            }), _jsx(_components.span, {
              className: "hljs-string",
              children: "' | append: '"
            }), "]"]
          }), "' -%}\n{{ value | parse }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can replace ", _jsx(_components.code, {
          children: "'\"value1\",\"value2\"'"
        }), " with the values you wish to insert in your static array."]
      }), "\n", _jsx(_components.h4, {
        id: "sync-an-empty-array",
        children: "Sync an empty array"
      }), "\n", _jsx(_components.p, {
        children: "You can sync an empty array to your destination by using this Liquid snippet:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["{{ ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'[]'"
          }), " | parse }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you decide to use this with ", _jsx(_components.a, {
          href: "#conditional-output",
          children: "Liquid conditionals"
        }), " or other complex syntax, make sure to ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/basics/whitespace/",
          children: "use a hyphen"
        }), " in your tag syntax to strip whitespace."]
      }), "\n", _jsx(_components.h4, {
        id: "convert-an-array-to-a-string",
        children: "Convert an array to a string"
      }), "\n", _jsxs(_components.p, {
        children: ["You can convert an array to a string by using the ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/filters/join/",
          children: _jsx(_components.code, {
            children: "join"
          })
        }), " function:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'items'"
            }), "]"]
          }), " | join: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " }}\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "json-function",
        children: "JSON function"
      }), "\n", _jsx(_components.p, {
        children: "You can convert an object to a JSON-formatted string by using this Liquid snippet:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'invoice'"
            }), "]"]
          }), " | json }}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can also use this function to escape special characters when creating a JSON payload."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if you attempt to map this value, ", _jsx(_components.code, {
          children: "This is the \"value\" in the column"
        }), " in the ", _jsx(_components.code, {
          children: "special_characters"
        }), " column, it will fail:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "\"column_mapping\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"{{ row.special_characters }}\""
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To successfully sync this value, select the ", _jsx(_components.code, {
          children: "json"
        }), " function and use the example format below:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "\"column_mapping\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " row.special_characters | json ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "isolate-object-properties",
        children: "Isolate object properties"
      }), "\n", _jsxs(_components.p, {
        children: ["If your model columns contain ", _jsx(_components.code, {
          children: "object"
        }), " type values, you may want to sync only one or some of the object's properties to your destination.\nYou can do this in the template mapper using the ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Property_(programming)#Dot_notation",
          children: "dot notation"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Specifically, you can write a Liquid expression containing the model column's name and the property's name: ", _jsx(_components.code, {
          children: "{{ row['model_column'].property }}"
        })]
      }), "\n", _jsxs(Warning, {
        children: [_jsxs(_components.p, {
          children: ["You can use dot notation for ", _jsx(_components.a, {
            href: "#map-columns-to-fields",
            children: "field mapping"
          }), " and\n", _jsx(_components.a, {
            href: "/syncs/record-matching",
            children: "record matching"
          }), ", but only for destinations that\nsupport Liquid templating."]
        }), _jsx("br", {}), _jsxs(_components.p, {
          children: ["Furthermore, dot notation doesn't work when ", _jsx(_components.a, {
            href: "/syncs/overview#test-a-row",
            children: "testing a row"
          }), ".\nTo test your sync, trigger a sync run and take a look at the ", _jsx(_components.a, {
            href: "/syncs/debugger#use-the-live-debugger",
            children: "live debugger"
          }), "."]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, suppose you had a model column named ", _jsx(_components.code, {
          children: "invoice"
        }), " that contains objects with this structure:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"invoice_number\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"INV-001\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"issue_date\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-06-08\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"due_date\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-06-30\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"John Doe\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"johndoe@example.com\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"address\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123 Main St, City, State, ZIP\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"items\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Product A\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"quantity\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"unit_price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10.99"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"total_price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "21.98"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"description\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Product B\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"quantity\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"unit_price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "15.99"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"total_price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "15.99"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"subtotal\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "37.97"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"tax\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "3.6"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"total\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "41.57"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"currency\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"GBP\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You may only want to map the ", _jsx(_components.code, {
          children: "total"
        }), " column to a ", _jsx(_components.code, {
          children: "price"
        }), " field in your destination.\nYou could do this by including the following Liquid in the template mapper:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'invoice'"
            }), "]"]
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".total"
          }), " }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use this to map nested properties, for example, ", _jsx(_components.code, {
          children: "customer.name"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ row", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'invoice'"
            }), "]"]
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".customer"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".name"
          }), " }}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "If you want to sync multiple object properties individually, you can use these types of expressions for multiple mappings in your sync configuration."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "mapping-data/mapping-data-dot-notation.png",
          alt: "Template Mapping"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure you've set the model column's type as ", _jsx(_components.code, {
          children: "Object / Array"
        }), " in the ", _jsxs(_components.a, {
          href: "/models/data-types-casting#casting-from-your-model-configuration",
          children: ["model's ", _jsx(_components.strong, {
            children: "Columns"
          }), " tab"]
        }), ".\nOtherwise, Hightouch syncs an ", _jsx(_components.code, {
          children: "undefined"
        }), " value to your destination."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Dot notation only works with model columns that contain single objects. For\narrays of objects, you can use the ", _jsx(_components.a, {
            href: "/syncs/mapping-data#inline-mapping",
            children: "inline\nmapper"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "spread-json-columns-into-fields",
        children: "Spread JSON columns into fields"
      }), "\n", _jsx(_components.p, {
        children: "If you manage many destination fields in warehouse SQL, you can sync a JSON (object) column and have Hightouch send each of its keys as a separate destination field. This saves you from mapping hundreds of fields by hand. The keys and values come directly from the column."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, a ", _jsx(_components.code, {
          children: "lead_json"
        }), " column containing this object:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Mohan\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"tier\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"gold\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"region\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"EMEA\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["syncs three fields (", _jsx(_components.code, {
          children: "first_name"
        }), ", ", _jsx(_components.code, {
          children: "tier"
        }), ", and ", _jsx(_components.code, {
          children: "region"
        }), ") to the destination, alongside any fields you map normally."]
      }), "\n", _jsx(_components.p, {
        children: "To spread a column:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the sync's mapping section, find ", _jsx(_components.strong, {
            children: "Spread JSON columns into fields"
          }), ". This option appears on destinations that support it."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add JSON column"
          }), " and choose the source column that holds the JSON object."]
        }), "\n", _jsx(_components.li, {
          children: "Save your configuration."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Regular field mappings always take precedence. If you map a field explicitly and the same field also appears in a spread column, the explicit mapping wins."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch sends each key exactly as it appears in the JSON object, so every\nkey must match a field name the destination accepts (after any ", _jsx(_components.a, {
            href: "#transform-spread-keys-and-values",
            children: "key or value\ntransforms"
          }), "). For consistent results, make\nsure every row's JSON object uses the same set of keys."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-precedence-for-multiple-columns",
        children: "Set precedence for multiple columns"
      }), "\n", _jsx(_components.p, {
        children: "You can spread more than one JSON column into the same destination. Hightouch applies them from top to bottom. If two columns contain the same key, the lower column wins. Drag the handle to the left of a column to change its order."
      }), "\n", _jsx(_components.h3, {
        id: "transform-spread-keys-and-values",
        children: "Transform spread keys and values"
      }), "\n", _jsxs(_components.p, {
        children: ["Because Hightouch sends keys and values verbatim, each key must match the destination's field name and each value must be in the shape the destination expects. When they don't match, apply a transform to every key or every value in the column instead of reshaping data in your model. For example, your warehouse might use ", _jsx(_components.code, {
          children: "snake_case"
        }), " keys while the destination's API field names have no underscores, or it might wrap each value in an object like ", _jsx(_components.code, {
          children: "{ \"dtype\": \"Integer\", \"value\": 202624 }"
        }), " when the destination wants the scalar ", _jsx(_components.code, {
          children: "202624"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "To apply a transform:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Select the settings icon to the right of the JSON column."
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Key transform"
          }), " or ", _jsx(_components.strong, {
            children: "Value transform"
          }), ", choose a transform:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Remove special characters"
              }), " — removes everything except letters and numbers. For example, ", _jsx(_components.code, {
                children: "SF_ME_PRC_BASE_PRICE"
              }), " becomes ", _jsx(_components.code, {
                children: "SFMEPRCBASEPRICE"
              }), "."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Custom Liquid template"
              }), " — rewrites the key or value with a ", _jsx(_components.a, {
                href: "/syncs/mapping-data#liquid-reference",
                children: "Liquid"
              }), " template. Reference both sides as ", _jsx(_components.code, {
                children: "{{ key }}"
              }), " and ", _jsx(_components.code, {
                children: "{{ value }}"
              }), ". For example, ", _jsx(_components.code, {
                children: "{{ key | remove: \"_\" | downcase }}"
              }), " turns ", _jsx(_components.code, {
                children: "SF_ME_PRC_BASE_PRICE"
              }), " into ", _jsx(_components.code, {
                children: "sfmeprcbaseprice"
              }), ". Liquid output is always a string."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Extract from object"
              }), " — pulls a field out of an object-shaped value and preserves its type. Enter a dot-separated path. For example, the path ", _jsx(_components.code, {
                children: "value"
              }), " extracts the number ", _jsx(_components.code, {
                children: "202624"
              }), " from ", _jsx(_components.code, {
                children: "{ \"dtype\": \"Integer\", \"value\": 202624 }"
              }), ". If a row's value isn't an object or is missing the path, Hightouch rejects that row. This transform is available on the value side only."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsx(_components.li, {
          children: "Save your configuration."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Each column applies at most one key transform and one value transform. ", _jsx(_components.strong, {
          children: "Remove special characters"
        }), " and ", _jsx(_components.strong, {
          children: "Custom Liquid template"
        }), " work on both sides; ", _jsx(_components.strong, {
          children: "Extract from object"
        }), " applies to values only."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Marketo matches REST API field names without regard to case but not\npunctuation. ", _jsx(_components.strong, {
            children: "Remove special characters"
          }), " lets a ", _jsx(_components.code, {
            children: "snake_case"
          }), " warehouse\ncolumn line up with Marketo's field names without renaming columns in your\nmodel."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "A transform runs for every key or value in the column on every row, so a\ncomplex Custom Liquid template over columns with many keys can slow down\nsyncs."
        })
      }), "\n", _jsx(_components.h2, {
        id: "verify-the-mapping",
        children: "Verify the mapping"
      }), "\n", _jsx(_components.p, {
        children: "Before you rely on the sync, confirm that each field lands with the value and shape you expect."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the sync configuration page, click ", _jsx(_components.strong, {
            children: "Test"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Choose a representative row, including one that exercises any template or inline mapping."
        }), "\n", _jsx(_components.li, {
          children: "Run the test and review the request Hightouch would send."
        }), "\n", _jsx(_components.li, {
          children: "Confirm each destination field holds the expected value, and that objects and arrays match the structure the destination requires."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["A sync test doesn't evaluate every mapping — dot notation, for example, resolves only on a real run. When a test can't confirm a value, trigger a sync and inspect the payload in the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "live debugger"
        }), ", then check the destination record to confirm the values arrived."]
      }), "\n", _jsx(_components.h2, {
        id: "field-mapping-updates",
        children: "Field mapping updates"
      }), "\n", _jsxs(_components.p, {
        children: ["If you edit the field mappings in your sync configuration, the sync engine automatically reprocesses the entire ", _jsx(_components.a, {
          href: "/models/creating-models#preview-model-results",
          children: "model query result set"
        }), " during the next sync run. Depending on the selected ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync mode"
        }), ", Hightouch syncs the model rows detected as ", _jsx(_components.strong, {
          children: "added"
        }), " or as ", _jsx(_components.strong, {
          children: "changed"
        }), " by ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture"
        }), " to your destination."]
      }), "\n", _jsxs(_components.p, {
        children: ["If the sync uses either ", _jsx(_components.strong, {
          children: "update"
        }), " or ", _jsx(_components.strong, {
          children: "upsert"
        }), " mode, Hightouch resyncs previously synced rows (with the updated mappings) as \"changed\" rows. If the sync uses ", _jsx(_components.strong, {
          children: "insert"
        }), " mode, Hightouch doesn't reprocess previously synced rows. Instead, it only syncs model rows that are detected as ", _jsx(_components.strong, {
          children: "added"
        }), " after the field mapping change was saved in your sync configuration."]
      }), "\n", _jsx(_components.h3, {
        id: "model-column-changes",
        children: "Model column changes"
      }), "\n", _jsxs(_components.p, {
        children: ["Most changes to your ", _jsx(_components.a, {
          href: "/models/creating-models",
          children: "model columns"
        }), " aren't automatically detected by sync configurations.\nHightouch handles the changes differently depending on whether columns are added, renamed, or removed from your model definition, or if you change a column's data type."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you add a new column to your model, Hightouch doesn't automatically add it as a field mapping to your sync configurations. This is because Hightouch doesn't know which columns you would like to sync to the destination, and which destination fields they should be mapped to. Follow the ", _jsx(_components.a, {
          href: "#map-columns-to-fields",
          children: "field mapping"
        }), " instructions to include new columns in your sync configuration."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You need to ", _jsx(_components.a, {
            href: "/models/creating-models#preview-model-results",
            children: "preview and save"
          }), "\nyour model to have new columns appear in the Hightouch UI."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you rename a column in your model, all syncs that had that column mapped in their configuration fail with an ", _jsx(_components.code, {
          children: "Unknown field name"
        }), " error.\nThe error message also contains the original name of the renamed column. To resolve this, remove or update the relevant field mappings in your sync configuration."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Changes to model column names aren't detected for ", _jsxs(_components.a, {
            href: "/syncs/types-and-modes#sync-modes",
            children: [_jsx(_components.strong, {
              children: "All"
            }), " /\n", _jsx(_components.strong, {
              children: "Mirror"
            })]
          }), " type syncs."]
        })
      }), "\n", _jsx(_components.p, {
        children: "If you remove a column from your model, Hightouch doesn't automatically remove it from the field mappings in the sync configuration. Make sure to remove the mapping manually."
      }), "\n", _jsxs(_components.p, {
        children: ["If you change a model column's ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "data type"
        }), ", the sync engine considers this update as a ", _jsx(_components.a, {
          href: "#field-mapping-updates",
          children: "field mapping update"
        }), " and reprocesses all model rows during the next sync run."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["As explained in the ", _jsx(_components.a, {
            href: "/models/creating-models#updating-a-primary-key",
            children: "primary key\nupdates"
          }), " section, if you\nupdate a model's primary key by selecting a different column, you need to\ntrigger a ", _jsx(_components.a, {
            href: "/syncs/resync-reset-clear#full-resync",
            children: "full resync"
          }), " or reset change\ndata capture (CDC) for all syncs that use that model. Otherwise, ", _jsx(_components.a, {
            href: "/syncs/cdc",
            children: "change data\ncapture"
          }), " can't process your model data correctly, which can make\nyour syncs fail."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "destination-field-changes",
        children: "Destination field changes"
      }), "\n", _jsx(_components.p, {
        children: "If you rename a field in your destination, you need to update the mapping to this field in your sync configuration. Select the new field name from the dropdown menu and save your changes."
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Set when the sync runs in ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-ui",
            children: "schedule a sync"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Resolve a type mismatch between a column and a field with ", _jsx(_components.a, {
            href: "/models/data-types-casting",
            children: "data types and casting"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Check a run and troubleshoot rejected rows in ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "debug a sync run"
          }), "."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
